import React, { useState } from 'react';
import Modal2 from 'components/_shared/Modal2';
import MemberStatus from './components/MemberStatus';
import MemberDetails from 'components/holders_list/components/EligibilityComponents/MemberDetails';
import AlertBody from './components/AlertBody';
import { CTAButton3 } from 'components/_shared/buttons';
import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';
import { Homeless, MemberSearchResult } from 'types';
import { reinstateDirectly } from 'api/homelesses';
import {
    decodeApiErrorFromResponse,
    parseApiErrorMessage
} from 'util/parseApiErrorMessage';

const useStyles = makeStyles(() => ({
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '32px'
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginTop: '40px'
    },
    closeButton: {
        color: '#7378E8',
        fontSize: '14px',
        fontWeight: '600',
        textTransform: 'none !important' as const
    },
    btnDiv: {
        display: 'flex',
        justifyContent: 'center'
    },
    errorText: {
        color: '#F5577A',
        fontSize: '14px',
        fontWeight: 700,
        textAlign: 'center'
    }
}));

const iconStyle = {
    backgroundColor: '#E7EFFE',
    border: '2px solid #062D7A',
    color: '#062D7A',
    fontSize: '24px'
};

const greenIconStyle = {
    backgroundColor: '#E9FBF2',
    border: '2px solid #1B9757',
    color: '#1B9757',
    fontSize: '24px'
};

type DirectReinstatePopupProps = {
    member: Homeless | MemberSearchResult;
    closeButtonText: string;
    onClose: () => void;
    onFinish: () => void;
};

const DirectReinstatePopup: React.FC<DirectReinstatePopupProps> = ({
    member,
    closeButtonText,
    onClose,
    onFinish
}) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [errorText, setErrorText] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const memberName =
        member.pii_first_name && member.pii_last_name
            ? `${member.pii_first_name} ${member.pii_last_name}`
            : member.name || '-';

    const handleReinstate = async () => {
        setLoading(true);
        setErrorText(null);

        try {
            const response = await reinstateDirectly(member.id);

            if (response.ok) {
                setSuccess(true);
            } else {
                setErrorText(await decodeApiErrorFromResponse(response));
            }
        } catch (error: unknown) {
            setErrorText(parseApiErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal2
            onClose={success ? onFinish : onClose}
            additionalStyles={{ iconStyle: success ? greenIconStyle : iconStyle }}
            iconName={success ? 'fa-check' : ''}
            title="Reinstate"
        >
            {success ? (
                <AlertBody
                    title="Member has been reinstated"
                    subtext={null}
                    titleColor="green"
                    closeButtonText={closeButtonText}
                    onClose={onFinish}
                />
            ) : (
                <div className={classes.body}>
                    <MemberStatus
                        photo={member.photo}
                        icon="far fa-check"
                        title={`${memberName} can be reinstated`}
                        subtext={`${memberName}'s membership can be reinstated now.`}
                        colorType="green"
                    />
                    <MemberDetails member={member} />
                    <div className={classes.buttonContainer}>
                        <CTAButton3 disabled={loading} onClick={handleReinstate}>
                            {loading ? 'Reactivating...' : 'Reactivate Membership'}
                        </CTAButton3>
                        {errorText && (
                            <span className={classes.errorText}>{errorText}</span>
                        )}
                    </div>
                    <div className={classes.btnDiv}>
                        <Button
                            className={classes.closeButton}
                            disabled={loading}
                            onClick={onClose}
                        >
                            {closeButtonText}
                        </Button>
                    </div>
                </div>
            )}
        </Modal2>
    );
};

export default DirectReinstatePopup;
